import { motion } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import heroVideo from "@/assets/hero-video.mp4";
import { useState, useRef, useEffect } from "react";

export function HeroSection() {
  const { t, language } = useLanguage();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const title3 = t("hero.title3");
  const hasTitle3 = title3 && !title3.includes("hero.");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Simulate loading progress
    if (!videoLoaded) {
      const interval = setInterval(() => {
        setLoadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(interval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [videoLoaded]);

  const handleVideoLoaded = () => {
    setLoadProgress(100);
    setTimeout(() => setVideoLoaded(true), 300);
  };

  const stats = [
    { value: t("hero.stat1Value"), label: t("hero.stat1Label") },
    { value: t("hero.stat2Value"), label: t("hero.stat2Label") },
    { value: t("hero.stat3Value"), label: t("hero.stat3Label") },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero pt-20">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "1.5s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-secondary/10 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-secondary/5 rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-sm font-medium mb-6"
            >
              <Zap className="w-4 h-4" />
              {t("hero.badge")}
            </motion.div>

            <h1 className="flex flex-col items-center lg:items-start gap-3 text-3xl md:text-4xl lg:text-[2.75rem] xl:text-[2.875rem] font-bold leading-none mb-5">
              <span className="text-foreground leading-[1.08] whitespace-nowrap">{t("hero.title1")}</span>
              <span className="text-secondary leading-[1.08]">
                {t("hero.title2")}
                {hasTitle3 && <span className="text-foreground">{title3}</span>}
              </span>
            </h1>

            <p className="min-h-14 text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
              {t("hero.desc")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="https://www.deep-insights.ai/book-demo" target="_blank" rel="noopener noreferrer">
                <Button variant="hero" size="lg">
                  {t("hero.cta1")}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <a href="https://www.deep-insights.ai/book-demo" target="_blank" rel="noopener noreferrer">
                <Button variant="hero-outline" size="lg">
                  {t("hero.cta2")}
                </Button>
              </a>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-border"
            >
              {stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-secondary">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - AI Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:flex items-center justify-center"
          >
            <div className="relative w-[576px] h-[576px]">
              {/* Central AI Hub */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 border-2 border-dashed border-secondary/20 rounded-full"
              />
              
              <div className="absolute inset-4 rounded-full overflow-hidden shadow-glow">
                {/* Loading Progress Indicator */}
                {mounted && !videoLoaded && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 backdrop-blur-xs z-10">
                    {/* Circular Progress */}
                    <div className="relative w-32 h-32">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          fill="none"
                          stroke="hsl(var(--secondary) / 0.2)"
                          strokeWidth="6"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          fill="none"
                          stroke="hsl(var(--secondary))"
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 56}
                          strokeDashoffset={2 * Math.PI * 56 * (1 - loadProgress / 100)}
                          className="transition-all duration-300 ease-out"
                        />
                      </svg>
                      {/* Percentage Text */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold text-secondary">
                          {Math.round(loadProgress)}%
                        </span>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
                  </div>
                )}
                <video 
                  ref={videoRef}
                  src={heroVideo}
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  onLoadedData={handleVideoLoaded}
                  className={`w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
                />
              </div>
            </div>

          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-muted-foreground/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1 h-2 bg-secondary rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
}
